import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import serialize from 'serialize-javascript';
import Stickyfill from 'stickyfill';
import get from 'lodash.get';

import Breakpoints from 'lib/Breakpoints';
import { adReady } from 'lib/adReady';
import amp from 'lib/amp';
import AdSizes from 'lib/AdSizes.json';
import $t from 'lib/trackingService';
import { isTelemundoVertical } from 'lib/vertical';

const FIRST_AD_TRACKING_EVENT = 'ramen_ads_first_queue_time';

if (typeof $t !== 'undefined') {
  $t('register', FIRST_AD_TRACKING_EVENT, { allowDuplicate: true });
}

const block = 'ad';

const mapStateToProps = ({
  front: { curation: { adsDisabled, pageRoute } },
  shared: { enigmaConfig: { vendors, targeting }, pageView },
  router: { path },
}) => {
  const { gptAdUnitPath = '' } = vendors || {};
  const { targeting: targetingForMPS = '' } = targeting || {};

  const ampTargeting = targetingForMPS.replace(/\|/g, ',');

  return {
    ampAdUnitPath: gptAdUnitPath,
    ampTargeting,
    adsDisabled,
    pageView,
    pageRoute,
    path,
  };
};

@connect(mapStateToProps)
@amp((props) => {
  let liveBlogProps;

  const {
    liveBlogItem, adClass, ampAdUnitPath, ampTargeting,
  } = props;

  if (liveBlogItem) {
    liveBlogProps = {
      id: props.id,
      'data-sort-time': Date.now(),
    };
  }

  if (/^boxinline/.test(props.slot)) {
    let targetSite = 'nbcnews-bento';

    if (isTelemundoVertical(props.vertical)) {
      targetSite = 'telemundo-bento';
    }
    return (
      /* eslint-disable react/jsx-indent */
      <div className={classNames('amp-ad', adClass)} {...liveBlogProps}>
        <amp-ad
          data-test="amp-ad"
          width="300"
          height="250"
          data-multi-size="300x250,300x50"
          data-multi-size-validation="false"
          data-loading-strategy="prefer-viewability-over-views"
          type="doubleclick"
          data-slot={`${ampAdUnitPath}/amp`}
          json={JSON.stringify({
            targeting: {
              amp: true,
              pos: 'boxinline_bentoarticle_amp',
              targeting: ampTargeting,
            },
          })}
          rtc-config={`{
            "urls": [
              "https://mps.nbcuni.com/request/page/json/device?path=/news/amp/ncna1000856&site=${targetSite}&type=amparticle&cat=amp|news&field[slotname]=amparticle&field[context]=amp&field[platform]=mobile&field[canurl]=CANONICAL_URL"
            ],
            "vendors": {
              "aps": {"PUB_ID":"3219","PARAMS":{"amp":"1"}},
              "indexexchange": {"SITE_ID":"326091"}
            }
          }`}
        />
      </div>
      /* eslint-enable react/jsx-indent */
    );
  }
  return null;
})
class Ad extends React.Component {
  static isFirstAdTracked = false;

  enigmaAd = null;

  static propTypes = {
    slot: PropTypes.string.isRequired,
    targeting: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    renderOnView: PropTypes.bool,
    handler: PropTypes.func,
    adClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    sticky: PropTypes.bool,
    offset: PropTypes.number,
    packageId: PropTypes.string,
    refreshInterval: PropTypes.number,
    adsDisabled: PropTypes.bool,
    activeTab: PropTypes.bool,
    pkgClassName: PropTypes.string,
    pageView: PropTypes.string,
    pageRoute: PropTypes.string,
    offsetViewport: (props, propName, componentName) => {
      // only validate offsetViewport for boxinline.
      const { slot } = props;
      const value = props[propName];
      if (slot === 'boxinline' && (!value || Number.isNaN(value))) {
        return new Error(`Invalid prop ${propName} supplied to ${componentName}`);
      }
      return null;
    },
  };

  static defaultProps = {
    targeting: {},
    renderOnView: true,
    handler: undefined,
    adClass: null,
    sticky: false,
    offset: undefined,
    packageId: null,
    refreshInterval: undefined,
    adsDisabled: false,
    activeTab: true,
    pkgClassName: block, // ad can be either a package or regular component
    offsetViewport: undefined,
    pageView: '',
    pageRoute: '',
  };

  // eslint-disable-next-line consistent-return
  componentDidMount() {
    const { adConfigEl } = this;
    const { handler, sticky, adsDisabled } = this.props;

    if (adsDisabled) return;

    adReady((Enigma, createAd) => {
      const ad = createAd(adConfigEl);

      if (typeof handler === 'function') {
        handler(ad, Enigma);
      }

      /*
       * TODO [wawjr3d][2/24/2018] when i test in IE 11/Win 7, there is a bug
       * where stickyfill doesnt stop sticking soon enough so ads hang over
       * other content. The TODO is to look deeper into what's wrong with
       * stickyfill or find a replacement library
       */
      ad.on(Enigma.EVENTS.RENDER, () => {
        try {
          if (!Ad.isFirstAdTracked) {
            Ad.isFirstAdTracked = this.trackFirstAd();
          }
        } catch (e) {
          if (typeof console === 'object' && typeof console.error === 'function') {
            console.error('ad.on() listener, error thrown while trying to track first ad: ', e);
          }
        }

        const stickyfill = Stickyfill();

        if (!sticky || !stickyfill) return;

        stickyfill.add(this.ad);
      });
    });
  }

  shouldComponentUpdate() {
    return false;
  }

  getPageView = () => {
    const { pageView, pageRoute } = this.props;
    if (pageView === 'front' && pageRoute === '/') {
      return 'cover';
    }
    return pageView;
  }

  getBreakpoint = () => {
    if (Breakpoints.isS()) {
      return 'mobile';
    }
    return 'desktop';
  }

  trackFirstAd = () => {
    // TODO figure out why the following error is sometimes thrown in IE,
    // why is adQueueTracker sometimes null/undefined:
    // TypeError: Unable to get property 'firstAdQueueTime' of undefined or null reference
    const firstAdQueueTimeGetter = get(window, 'adQueueTracker.firstAdQueueTime', () => {});
    const firstAdQueueTime = typeof firstAdQueueTimeGetter === 'function'
      ? firstAdQueueTimeGetter()
      : null;

    if (firstAdQueueTime) {
      const { queueTime, slot } = firstAdQueueTime;
      $t('track', FIRST_AD_TRACKING_EVENT, {
        slot,
        queueTime: Math.round(queueTime) / 1000,
        pageView: this.getPageView(),
        breakpoint: this.getBreakpoint(),
      });

      return true;
    }
    return false;
  }

  render() {
    const {
      adClass,
      slot,
      renderOnView,
      offset,
      refreshInterval,
      packageId,
      targeting,
      adsDisabled,
      activeTab,
      pkgClassName,
      offsetViewport,
      sticky,
    } = this.props;
    if (adsDisabled) return null;

    // putting a check here to make sure only 'boxinline' ad is able to pass the
    // offsetViewport property down and exclude other ads, such as boxrail
    const offsetViewportCheck = offsetViewport && slot === 'boxinline' ? offsetViewport : null;

    const sizes = AdSizes[slot] || [];

    let toolkitClasses = null;
    if (!['topbanner', 'sponsorlogo', 'interstitial', 'midresponsive'].includes(slot)) {
      toolkitClasses = ['tc', 'tl-m'];
    }

    const addAdContainer = !['topbanner', 'sponsorlogo', 'interstitial'].includes(slot);

    // TODO: we shouldn't be calling out slots here
    const adClasses = classNames(
      pkgClassName,
      adClass,
      toolkitClasses,
      {
        'ad-container': addAdContainer,
        'ad--sticky': sticky,
        'ad--front': packageId !== null,
        boxinlineAd: slot === 'boxinline',
      },
      'dn-print',
    );

    return (
      <div
        ref={(ad) => { this.ad = ad; }}
        className={adClasses}
        data-packageid={packageId}
        data-test="ad__container"
      >
        <div
          ref={(el) => { this.adConfigEl = el; }}
          data-enigma="true"
          data-slot={slot}
          data-sizes={serialize(sizes)}
          data-offset={offset}
          data-refresh-interval={refreshInterval}
          data-render-on-view={renderOnView}
          data-targeting={serialize(targeting)}
          data-active-tab={activeTab}
          data-offset-viewport={offsetViewportCheck}
        />
      </div>
    );
  }
}

export default Ad;
